/**
 * CoverageReport
 * ===========================================================================
 * The pre-render casting gate: "is my casting complete and voiced?" Reads
 * GET /voice-rendering/:bookId/casting-coverage and lists the speaking cast
 * unvoiced-first, with a one-click "auto-cast the unvoiced" action.
 * Self-contained (apiClient + local state); merge candidates are a follow-on.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Segment, Header, Icon, Button, Label, Message, Loader, List, Progress, Dropdown, Checkbox, Confirm } from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import { config } from '../../constants/config';

// ok() wraps as { success, data }.
const unwrap = (resp) =>
  resp && resp.data && resp.data.data !== undefined ? resp.data.data : resp && resp.data;

function CoverageReport({ bookId, litGroupUuid }) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [casting, setCasting] = useState(false);
  const [castMsg, setCastMsg] = useState(null);
  const [castPct, setCastPct] = useState(0);
  const [estimating, setEstimating] = useState(false);
  // { targets, estimate } — the priced cast awaiting confirmation. null = nothing pending.
  const [pendingCast, setPendingCast] = useState(null);
  /**
   * ⭐ THE TWO THINGS A VOICE IS MADE OF, readable before the spend: the BRIEF the designer is
   * sent, and the SAMPLE the voice will read. Operator’s call, 2026-10-18.
   *
   * ⓘ Fetched on demand rather than with the estimate. The brief is written by an LLM — a dozen
   * calls for a forty-character cast — so composing it on every open would make the price dialog
   * slow for the common case where the operator only wants the number.
   */
  const [briefs, setBriefs] = useState(null);        // the dry run’s proposals, in scope order
  const [briefsBusy, setBriefsBusy] = useState(false);
  const [briefsError, setBriefsError] = useState(null);
  // Reassign (B2): which unassigned lines to bind, and to whom.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState(null);
  const [assigning, setAssigning] = useState(false);
  // Pick-and-choose auto-cast: selected character uuids for a targeted (re-)voice.
  const [castSel, setCastSel] = useState(() => new Set());

  // Live casting progress via SSE (mirrors the re-ingest button + TalentAgency).
  const userState = useSelector((s) => s.user);
  const userData = userState?.data || userState?.profile?.data || userState || {};
  const userId = userData.userId || userData.id;
  const esRef = useRef(null);
  const closeEs = () => {
    if (esRef.current) { try { esRef.current.close(); } catch (_) {} esRef.current = null; }
  };
  useEffect(() => closeEs, []);

  const load = useCallback(async () => {
    if (!bookId) { setCoverage(null); return; }
    setLoading(true); setError(null);
    try {
      const resp = await apiClient.get(`/voice-rendering/${bookId}/casting-coverage`);
      setCoverage(unwrap(resp));
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load coverage');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { load(); }, [load]);

  // Targeted (re-)voice of an explicit character set. Both the bulk
  // "Auto-cast N unvoiced" button and the pick-and-choose "Voice selected"
  // button funnel through here. Re-voicing an already-designed character is
  // honored server-side when targets are explicit (real VA recordings are
  // skipped there).
  const runAutoCast = async (targets, reviewedBriefs) => {
    if (!litGroupUuid || !Array.isArray(targets) || targets.length === 0) return;
    setCasting(true); setCastMsg('Starting…'); setCastPct(3); setError(null);

    // Subscribe BEFORE firing so a fast finish isn't missed. Keep `casting` true
    // until CASTING_COMPLETE / CASTING_ERROR arrives; auto-refresh coverage then.
    closeEs();
    if (userId) {
      try {
        const es = new EventSource(`${config.BASE_URL}/sse-endpoint?userId=${userId}`);
        esRef.current = es;
        es.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const d = parsed.data || parsed;
            if (d.litGroupUuid && d.litGroupUuid !== litGroupUuid) return;
            if (d.type === 'CASTING_PROGRESS') {
              setCastMsg(d.message || d.step || 'Casting…');
              if (typeof d.percent === 'number') setCastPct(d.percent);
            } else if (d.type === 'CASTING_COMPLETE') {
              closeEs(); setCasting(false); setCastPct(100);
              setCastSel(new Set());
              // When ElevenLabs refuses on a quota, show ITS sentence — this account has three
              // separate ceilings (voice slots, monthly add/edit operations, TTS characters)
              // and this message used to name the wrong one, which sends the operator to fix
              // something that isn't broken.
              if (d.capMessage) setError(`ElevenLabs stopped the run: ${d.capMessage}`);
              // A run that cast nothing usually means a prerequisite was missed, not that a
              // ceiling was hit. Say which — "Cast 0 voice(s)" alone points the operator at
              // the ElevenLabs cap, the expensive explanation, when the real answer is
              // normally that derivation has not been run.
              if (d.reason) { setCastMsg(`Nothing to cast — ${d.reason}.`); load(); return; }
              setCastMsg(
                `Cast ${d.attached ?? d.created ?? targets.length} voice(s)` +
                // Reused voices cost NONE of the 1040 monthly add/edit operations, which is a
                // ceiling that cannot be bought out of — same on Business as on Scale. Worth
                // showing: it is the only lever there is.
                (d.reused ? ` — ${d.reused} reused from earlier casting (no ElevenLabs quota used)` : '') +
                (d.stoppedAtCap
                  ? (d.capMessage ? ' — stopped by ElevenLabs (see above).' : ' — stopped early; run again for the rest.')
                  : '.')
              );
              load(); // self-refresh the coverage list
            } else if (d.type === 'CASTING_ERROR') {
              closeEs(); setCasting(false);
              setError(d.message || 'Casting failed');
            }
          } catch (_) { /* ignore keepalives */ }
        };
        es.onerror = () => { /* non-fatal; progress just won't stream live */ };
      } catch (_) { /* EventSource unavailable — falls back to Re-check */ }
    }

    try {
      await apiClient.post(`/litgroups/${litGroupUuid}/casting/auto-cast`, {
        targetUuids: targets,
        // ⓘ Omitted entirely when nothing was reviewed, so the server composes as before.
        ...(reviewedBriefs ? { briefs: reviewedBriefs } : {}),
      });
    } catch (e) {
      closeEs(); setCasting(false);
      setError(e.response?.data?.error?.message || e.response?.data?.error || e.message || 'Auto-cast failed to start');
    }
  };

  /**
   * ⭐ COST FIRST, SPEND SECOND — nothing reaches runAutoCast without passing through here.
   *
   * ⛔ A voice costs TWO of 1,040 monthly add/edit operations, the ceiling is identical on Business
   * and Scale, and it cannot be bought out of. Until this existed the ceiling was discovered only by
   * HITTING it, which cost half a month of Voice Design work. An overshoot is not a delay, it is a
   * month.
   *
   * ⚠️ The estimate never BLOCKS. If the budget cannot be read the cast is still allowed — the
   * warning simply cannot be shown, and saying so is more honest than inventing a number.
   */
  const requestCast = async (targets) => {
    setError(null);
    setEstimating(true);
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/casting/auto-cast/estimate`, { targetUuids: targets });
      setPendingCast({ targets, estimate: r.data });
    } catch (e) {
      // Could not price it — offer the cast anyway, and say the number is unknown.
      setPendingCast({ targets, estimate: null });
    } finally {
      setEstimating(false);
    }
  };

  /**
   * ⭐ THE DRY RUN, REUSED. `design-candidates/dry-run` already composes briefs and picks samples
   * and spends NOTHING — "Nothing was generated and nothing was saved. These are the prompts a run
   * would send." There is no second implementation here, which matters because a preview composed
   * differently from the run would be worse than no preview.
   */
  const composeBriefs = async () => {
    const targets = pendingCast?.targets || [];
    if (!targets.length) return;
    setBriefsBusy(true); setBriefsError(null);
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/casting/design-candidates/dry-run`, { targetUuids: targets });
      setBriefs(r.data?.proposals || []);
    } catch (e) {
      setBriefsError(e.response?.data?.error || e.message || 'Could not compose the briefs');
    } finally {
      setBriefsBusy(false);
    }
  };

  const autoCastUnvoiced = () => requestCast(coverage?.unvoicedUuids || []);
  const autoCastSelected = () => requestCast([...castSel]);

  if (!bookId) return null;

  const covered = coverage?.covered;
  const counts = coverage?.counts || { speaking: 0, voiced: 0, unvoiced: 0 };
  const chars = coverage?.characters || [];
  const unassigned = coverage?.unassignedLines?.count || 0;
  const unassignedSamples = coverage?.unassignedLines?.samples || [];
  const ready = !!covered && unassigned === 0;

  const unvoicedUuids = coverage?.unvoicedUuids || chars.filter((c) => !c.voiced).map((c) => c.uuid);
  const toggleCast = (uuid) => {
    if (!uuid) return;
    setCastSel((prev) => { const n = new Set(prev); if (n.has(uuid)) n.delete(uuid); else n.add(uuid); return n; });
  };

  const charByName = (name) => chars.find((c) => c.name === name);
  const speakerOptions = [
    ...(chars.some((c) => c.name === 'Narrator') ? [] : [{ key: '__narrator', value: 'Narrator', text: 'Narrator' }]),
    ...chars.map((c) => ({ key: c.uuid, value: c.name, text: c.name })),
  ];
  const toggleLine = (id) => {
    if (!id) return;
    setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const selectAllShown = () => setSelectedIds(new Set(unassignedSamples.map((s) => s.lineId).filter(Boolean)));
  const doReassign = async () => {
    const ids = [...selectedIds];
    if (!assignTo || ids.length === 0) return;
    setAssigning(true); setError(null);
    try {
      const ch = charByName(assignTo);
      await apiClient.post(`/voice-rendering/${bookId}/reassign-speaker`, {
        lineIds: ids, speakerName: assignTo, characterUuid: ch?.uuid || null,
      });
      setSelectedIds(new Set());
      await load(); // assigned lines drop off; counts update
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Reassign failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Segment className="coverage-report" style={{ marginTop: '1em' }}>
      <Header as="h5" style={{ marginBottom: '0.5em' }}>
        <Icon name="users" /> Casting Coverage
        {coverage && (ready
          ? <Label color="green" size="tiny" style={{ marginLeft: 8 }}><Icon name="check" /> Complete</Label>
          : <Label color="orange" size="tiny" style={{ marginLeft: 8 }}>
              {[counts.unvoiced ? `${counts.unvoiced} unvoiced` : null, unassigned ? `${unassigned} unassigned` : null].filter(Boolean).join(' · ')}
            </Label>)}
      </Header>

      {loading && <Loader active inline size="tiny" />}
      {error && (
        <Message negative size="tiny" onDismiss={() => setError(null)}>{String(error)}</Message>
      )}

      {coverage && !loading && (
        <>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
            {counts.voiced}/{counts.speaking} speaking character{counts.speaking === 1 ? '' : 's'} voiced.
          </div>

          <Button.Group size="tiny">
            {!covered && (
              <Button
                color="purple"
                loading={casting || estimating}
                disabled={casting || estimating || !litGroupUuid}
                onClick={autoCastUnvoiced}
                title="Generate + attach a designed voice for each unvoiced speaking character (spends ElevenLabs slots/ops)"
              >
                <Icon name="magic" /> Auto-cast {counts.unvoiced} unvoiced
              </Button>
            )}
            <Button basic onClick={load} disabled={loading || casting}>
              <Icon name="refresh" /> Re-check
            </Button>
          </Button.Group>

          {casting && (
            <Progress percent={castPct} size="tiny" color="purple" active style={{ marginTop: 8, marginBottom: 4 }}>
              {castMsg}
            </Progress>
          )}
          {!casting && castMsg && (
            <Message positive size="tiny" style={{ marginTop: 6 }} onDismiss={() => setCastMsg(null)}>
              {castMsg}
            </Message>
          )}

          {chars.length > 0 ? (
            <>
              {/* Pick-and-choose: (re-)voice a hand-picked set — e.g. one character missed in a bulk pass. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <Button.Group size="mini">
                  <Button basic disabled={casting || unvoicedUuids.length === 0}
                    onClick={() => setCastSel(new Set(unvoicedUuids))}>
                    Select unvoiced ({unvoicedUuids.length})
                  </Button>
                  <Button basic disabled={casting || castSel.size === 0} onClick={() => setCastSel(new Set())}>
                    Clear
                  </Button>
                </Button.Group>
                <Button size="mini" color="purple" loading={casting || estimating}
                  disabled={casting || !litGroupUuid || castSel.size === 0}
                  onClick={autoCastSelected}
                  title="Generate + attach a designed voice for each selected character; re-selecting a voiced one re-rolls it (spends ElevenLabs slots/ops)">
                  <Icon name="magic" /> Voice / re-voice {castSel.size || ''} selected
                </Button>
              </div>
              <List divided size="small" style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
                {chars.map((c) => (
                  <List.Item key={c.uuid}>
                    <List.Content floated="right">
                      {c.voiced
                        ? <Label size="mini" color="green" basic><Icon name="microphone" /> voiced</Label>
                        : <Label size="mini" color="orange"><Icon name="warning sign" /> no voice</Label>}
                    </List.Content>
                    <Checkbox
                      checked={castSel.has(c.uuid)}
                      disabled={casting || !c.uuid}
                      onChange={() => toggleCast(c.uuid)}
                      label={
                        <label style={{ fontSize: 13 }}>
                          {c.name}
                          <span style={{ color: '#999', fontSize: 11 }}> · {c.lines} line{c.lines === 1 ? '' : 's'}</span>
                        </label>
                      }
                    />
                  </List.Item>
                ))}
              </List>
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>No speaking characters found for this book.</div>
          )}

          {unassigned > 0 && (
            <Segment secondary style={{ marginTop: 10, padding: '8px 10px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#b26a00', marginBottom: 4 }}>
                <Icon name="question circle" /> {unassigned} line{unassigned === 1 ? '' : 's'} with no speaker — assign a speaker so they render.
              </div>
              <List size="small" style={{ maxHeight: 260, overflowY: 'auto', margin: 0 }}>
                {unassignedSamples.map((s, i) => (
                  <List.Item key={s.lineId || i} style={{ padding: '4px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {/* Hint badge, ahead of the checkbox so the eye hits the verdict first.
                          The server only sets `hint` where the evidence is mechanical (a sign
                          or a quoted list), so a badge here means "this one is decided" and a
                          blank gutter means "read the context below". Fixed width keeps the
                          checkboxes on a single vertical rule either way.
                          NOTE: `size` is inert on Semantic Labels in this build — the CSS
                          ships no size variants — so this is styled, not sized by prop. */}
                      <span style={{ flex: '0 0 62px', paddingTop: 1 }}>
                        {s.hint && (
                          <Label
                            title={s.hint}
                            style={{
                              fontSize: 9, padding: '2px 5px', margin: 0, lineHeight: 1.3,
                              background: '#fff3e0', color: '#b26a00', border: '1px solid #ffd699',
                              cursor: 'help', whiteSpace: 'nowrap'
                            }}
                          >
                            <Icon
                              name={/written text/.test(s.hint) ? 'file alternate outline' : 'quote right'}
                              style={{ margin: '0 3px 0 0' }}
                            />
                            Narrator?
                          </Label>
                        )}
                        {/* Who SAID it, where the narration immediately after names them.
                            A suggestion, never an assignment — the surrounding text is shown
                            right below so the call stays with a human. The server proposes
                            only real characters, and only on an attribution pattern; a name
                            merely mentioned nearby produces nothing. */}
                        {s.suggestedSpeaker && (
                          <Label
                            title={s.suggestedWhy || ''}
                            style={{
                              fontSize: 9, padding: '2px 5px', margin: 0, lineHeight: 1.3,
                              background: '#e8f5e9', color: '#1b5e20', border: '1px solid #a5d6a7',
                              cursor: 'help', whiteSpace: 'nowrap'
                            }}
                          >
                            <Icon name="user outline" style={{ margin: '0 3px 0 0' }} />
                            {s.suggestedSpeaker}?
                          </Label>
                        )}
                      </span>

                      <Checkbox
                        checked={!!s.lineId && selectedIds.has(s.lineId)}
                        disabled={!s.lineId || assigning}
                        onChange={() => toggleLine(s.lineId)}
                        label={
                          <label style={{ fontSize: 12 }}>
                            <span style={{ color: '#999', fontSize: 11 }}>ch {s.chapter ?? '–'} · {s.type}</span>{' '}
                            {s.text}
                            {/* The line before is where the answer usually is — "And this sign
                                didn't point anywhere. It said only:" decides "Humbecker Ford
                                1.8 miles." on its own. Showing it here is the whole point of
                                the change; without it the badge is a bare assertion and every
                                unhinted line still means a trip to the manuscript. */}
                            {s.before?.text && (
                              <div style={{ color: '#8a8a8a', fontSize: 11, fontStyle: 'italic', marginTop: 2 }}>
                                <Icon name="level up alternate" flipped="horizontally" style={{ margin: '0 3px 0 0', opacity: 0.6 }} />
                                {s.before.text}
                              </div>
                            )}
                          </label>
                        }
                      />
                    </div>
                  </List.Item>
                ))}
              </List>
              {unassigned > unassignedSamples.length && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  …and {unassigned - unassignedSamples.length} more (re-check after assigning to load the next batch)
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="mini" basic onClick={selectAllShown} disabled={assigning}>Select all shown</Button>
                <Dropdown
                  selection search
                  placeholder="Assign to…"
                  options={speakerOptions}
                  value={assignTo}
                  onChange={(_, { value }) => setAssignTo(value)}
                  style={{ minWidth: 150 }}
                  disabled={assigning}
                />
                <Button
                  size="mini" primary loading={assigning}
                  disabled={assigning || !assignTo || selectedIds.size === 0}
                  onClick={doReassign}
                >
                  Assign {selectedIds.size || ''} to {assignTo || '…'}
                </Button>
              </div>
            </Segment>
          )}
        </>
      )}

      {/* ⛔ THE SPEND GATE. Every path to runAutoCast passes through requestCast and lands here. */}
      <Confirm
        open={!!pendingCast}
        header="Cast these voices?"
        content={(
          <CastCost
            pending={pendingCast}
            briefs={briefs}
            briefsBusy={briefsBusy}
            briefsError={briefsError}
            onCompose={composeBriefs}
          />
        )}
        confirmButton={
          pendingCast?.estimate?.free ? 'Attach (free)'
            : pendingCast?.estimate?.fits === false ? 'Cast anyway' : 'Cast'
        }
        onCancel={() => { setPendingCast(null); setBriefs(null); setBriefsError(null); }}
        onConfirm={() => {
          const t = pendingCast?.targets || [];
          /**
           * ⭐ WHAT WAS READ IS WHAT GETS SENT. Briefs composed in this dialog travel with the run,
           * so autoCast uses them instead of composing again at temperature. ⛔ Without this the
           * operator would approve one brief and the voice would be designed from another.
           */
          const reviewed = (briefs || []).reduce((acc, b) => {
            if (b && b.uuid && b.prompt) acc[b.uuid] = b.prompt;
            return acc;
          }, {});
          setPendingCast(null); setBriefs(null); setBriefsError(null);
          runAutoCast(t, Object.keys(reviewed).length ? reviewed : null);
        }}
      />
    </Segment>
  );
}

/**
 * What a cast will cost, in the currency that actually binds.
 *
 * ⚠️ OPERATIONS, NOT CHARACTERS AND NOT SLOTS. Measured on this account 2026-08-25: 3.79M TTS
 * characters spare, 330 of 660 voice slots free with 117 more reclaimable — against 1,040 add/edit
 * operations a month, two per voice, identical on Business ($990) and Scale ($299), and impossible
 * to buy more of. Naming the wrong ceiling sends the operator to fix something that is not broken,
 * which has already happened once in this component's own error handling.
 */
function CastCost({ pending, briefs, briefsBusy, briefsError, onCompose }) {
  if (!pending) return null;
  const e = pending.estimate;
  const n = pending.targets?.length || 0;

  // ⚠️ Unpriced is not a refusal. Say what is unknown and let the operator decide.
  if (!e) {
    return (
      <div className="content">
        <p>{n} character{n === 1 ? '' : 's'} selected.</p>
        <p style={{ color: '#b58105' }}>
          ⚠️ The remaining ElevenLabs operation budget could not be read, so this cast cannot be
          priced. It will still run — but if the monthly ceiling is close, it may stop partway.
        </p>
      </div>
    );
  }
  if (e.reason) {
    return <div className="content"><p>Nothing to cast — {e.reason}.</p></div>;
  }

  const pct = e.opsMax ? Math.round((e.opsNeeded / e.opsMax) * 100) : null;
  return (
    <div className="content">
      <p>
        <strong>{e.targets}</strong> character{e.targets === 1 ? '' : 's'} in scope
        {e.cappedAt ? <> — capped at {e.cappedAt} per run, {e.totalBeforeCap} in total</> : null}.
      </p>
      {e.reusable > 0 && (
        <p style={{ color: '#21ba45' }}>
          ♻️ <strong>{e.reusable}</strong> already have a voice you own and will be attached for{' '}
          <strong>free</strong>.
        </p>
      )}
      {e.free ? (
        <p style={{ color: '#21ba45' }}>
          ⭐ <strong>This cast costs nothing.</strong> Every character in scope is being reused.
        </p>
      ) : (
        <>
          <p>
            ⛔ <strong>{e.willBuy}</strong> need a new voice ={' '}
            <strong>{e.opsNeeded} operations</strong> of your{' '}
            {e.opsRemaining === null ? '(unknown)' : e.opsRemaining} remaining this month
            {pct !== null ? ` — ${pct}% of the monthly ceiling` : ''}.
          </p>
          {e.fits === false && (
            <p style={{ color: '#db2828' }}>
              ⛔ <strong>This does not fit.</strong> You are {e.shortfall} operation
              {e.shortfall === 1 ? '' : 's'} short, so the run will stop partway. The ceiling is the
              same on every tier and cannot be raised — it resets monthly.
            </p>
          )}
          {e.fits === true && (
            <p style={{ fontSize: '0.92em', color: '#666' }}>
              Fits, with {e.opsRemaining - e.opsNeeded} operation
              {e.opsRemaining - e.opsNeeded === 1 ? '' : 's'} left afterwards.
            </p>
          )}
          {/* ⚠️ Junk roster entries are bought like anyone else. Half of LN1's buy list was
              possessives, titles and classes — showing the names is the cheapest way to catch it. */}
          {e.buyList?.length > 0 && (
            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '0.5em' }}>
              Buying voices for: {e.buyList.map((b) => b.name).join(', ')}
              {e.willBuy > e.buyList.length ? `, and ${e.willBuy - e.buyList.length} more` : ''}.
              <div style={{ marginTop: '0.35em', fontStyle: 'italic' }}>
                Anything in that list that is not a person — a possessive, a title, a crowd — costs
                two operations and a permanent voice slot. Cancel and merge it first if you see one.
              </div>
            </div>
          )}
        </>
      )}
      <BriefPreview
        briefs={briefs}
        busy={briefsBusy}
        error={briefsError}
        onCompose={onCompose}
        count={e.willBuy}
      />
    </div>
  );
}

/**
 * The brief and the sample, per character, before anything is spent.
 *
 * ⭐⭐ TWO ELEMENTS, BOTH JUDGEABLE. The brief is what the voice designer is told; the sample is
 * what the voice reads back. A voice that comes out wrong is almost always one of those two being
 * wrong, and until now neither was visible until after the operations had been spent.
 *
 * ⚠️ THE SAMPLE SOURCE IS THE TELL. `own-line` means the character reads their own dialogue, which
 * is what makes three voices distinguishable. `stock` means no line of theirs reached the
 * provider’s 100-character floor, so they will read the same neutral paragraph as everybody else
 * who fell back — a weaker decision, and the one worth catching here rather than by ear later.
 */
function BriefPreview({ briefs, busy, error, onCompose, count }) {
  if (!count) return null;   // nothing is being bought; there is no brief to read

  if (!briefs) {
    return (
      <div style={{ marginTop: '0.75em', paddingTop: '0.6em', borderTop: '1px solid #eee' }}>
        <Button size="mini" basic loading={busy} disabled={busy} onClick={onCompose}>
          <Icon name="eye" />Read the brief and sample first
        </Button>
        <span style={{ fontSize: '0.82em', color: '#888', marginLeft: '0.6em' }}>
          Free — composes the prompts without generating anything.
        </span>
        {error && (
          <div style={{ color: '#db2828', fontSize: '0.85em', marginTop: '0.4em' }}>⛔ {error}</div>
        )}
      </div>
    );
  }

  const stock = briefs.filter((b) => b.sampleSource === 'stock').length;
  return (
    <div style={{ marginTop: '0.75em', paddingTop: '0.6em', borderTop: '1px solid #eee' }}>
      <div style={{ fontSize: '0.88em', marginBottom: '0.4em' }}>
        <strong>{briefs.length}</strong> brief{briefs.length === 1 ? '' : 's'} composed — these exact
        words will be sent.
        {stock > 0 && (
          <span style={{ color: '#b58105' }}>
            {' '}⚠️ {stock} will read stock text — no line of theirs reaches 100 characters.
          </span>
        )}
      </div>
      <div style={{ maxHeight: '17rem', overflowY: 'auto', fontSize: '0.84em' }}>
        {briefs.map((b) => (
          <div key={b.uuid} style={{ marginBottom: '0.6em', paddingBottom: '0.5em', borderBottom: '1px dotted #eee' }}>
            <div>
              <strong>{b.name}</strong>
              <span style={{ color: '#888' }}>
                {' '}· {b.archetype || 'no archetype'}{b.gender ? ` · ${b.gender}` : ''}
                {b.lines == null ? '' : ` · ${b.lines} lines`}
              </span>
            </div>
            <div style={{ color: '#444', marginTop: '0.2em' }}>
              <span style={{ color: '#888' }}>brief: </span>{b.prompt || '—'}
            </div>
            <div style={{ color: '#444', marginTop: '0.2em', fontStyle: 'italic' }}>
              <span style={{ color: '#888', fontStyle: 'normal' }}>
                reads ({b.sampleSource}
                {b.sampleOwnChars != null && b.sampleChars
                  ? `, ${b.sampleOwnChars} of ${b.sampleChars} chars their own`
                  : ''}
                ):{' '}
              </span>
              “{b.sampleText}”
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CoverageReport;
